import React from 'react';
import PropTypes from 'prop-types';
import Avatar from 'sharedComponents/Avatar';
import { CheckIcon, BagIcon, KeyActionIcon } from 'sharedComponents/Icons';
import { ListItemIcon, Typography } from '@material-ui/core';
import { getInitials, getPriceString, mapClientContacts, toTitleCase } from 'utils';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import { ClientCentralPropTypes, GroupClientsFiltersPropTypes } from 'utils/PropTypesValues';
import { clientAssetsQueryParams } from 'sharedComponents/FilterChips/filterUtils';

const isSelected = (id, selectedClients = []) => selectedClients.find(({ profileId }) => profileId === id);

const ClientListItemGeneral = ({
  classes,
  client,
  selectionEnabled,
  withAlphabet,
  customClientClickAction,
  allowClickOnIcon,
  selectedClients,
  selectClient,
  beforeGoToProfilePage,
  updateRecentClients,
  history,
  filters,
}) => {
  const onTextClick = (client) => {
    if (customClientClickAction) {
      return customClientClickAction(client);
    }

    beforeGoToProfilePage();
    updateRecentClients(client);
    history.push(`/clients/universal-profile/${client.profileId}/about`);
  };

  const onAvatarClick = (client) => {
    if (selectionEnabled) return selectClient(client);

    if (allowClickOnIcon) {
      return onTextClick(client);
    }
  };

  const clientAnnualSpendString = getPriceString(client?.annualSpend, true);
  const selectedAssets = filters?.withAssets?.split(',') || [];
  const showPointsAssetsBalance = selectedAssets.includes(clientAssetsQueryParams.point) && !!client.pointCardsBalance;
  const showPromoAssetsBalance = selectedAssets.includes(clientAssetsQueryParams.promo) && !!client.promoCardsBalance;

  return (
    <ListItem disableGutters key={client.profileId} className={classes.link}>
      <ListItemAvatar onClick={() => onAvatarClick(client)} data-testid="group-clients-select">
        {selectionEnabled && isSelected(client.profileId, selectedClients) ? (
          <Avatar className={classes.activeAvatar}>
            <CheckIcon className={classes.icon} />
          </Avatar>
        ) : (
          <Avatar className={classes.avatar}>{getInitials(`${client.firstName} ${client.lastName}`)}</Avatar>
        )}
      </ListItemAvatar>
      <ListItemText onClick={() => onTextClick(client)} data-testid="group-clients-details">
        <Typography color="textPrimary">{toTitleCase(`${client.firstName} ${client.lastName}`)}</Typography>
        <Typography className={classes.text} variant="body2" color="textSecondary">
          {mapClientContacts(client.phoneNumbers || client.phoneNumber, client.emails || client.email, '•')}
        </Typography>
        {clientAnnualSpendString && (
          <Typography variant="body2" color="textSecondary">
            Annual spend: {clientAnnualSpendString}
          </Typography>
        )}
        {showPointsAssetsBalance && (
          <Typography variant="body2" color="textSecondary" noWrap className={classes.assetsBalance}>
            Point card balance: {getPriceString(client.pointCardsBalance, true)}
          </Typography>
        )}
        {showPromoAssetsBalance && (
          <Typography variant="body2" color="textSecondary" noWrap className={classes.assetsBalance}>
            Promo card balance: {getPriceString(client.promoCardsBalance, true)}
          </Typography>
        )}
      </ListItemText>
      {client.connectedWithMe && (
        <ListItemIcon
          classes={{ root: withAlphabet ? classes.menuItemIconContactWithMe : classes.menuItemIconContactWithMeSmall }}
          data-testid="connect-icon"
        >
          <KeyActionIcon className={classes.icon} />
        </ListItemIcon>
      )}
      {client.conciergeItems && (
        <ListItemIcon
          classes={{ root: withAlphabet ? classes.menuItemIcon : classes.menuItemIconSmall }}
          data-testid="concierge-cart-icon"
        >
          <BagIcon className={classes.icon} />
        </ListItemIcon>
      )}
    </ListItem>
  );
};

ClientListItemGeneral.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  client: ClientCentralPropTypes,
  selectClient: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.object),
  selectionEnabled: PropTypes.bool,
  allowClickOnIcon: PropTypes.bool,
  beforeGoToProfilePage: PropTypes.func,
  updateRecentClients: PropTypes.func,
  filters: GroupClientsFiltersPropTypes,
};

ClientListItemGeneral.defaultProps = {
  selectClient: () => {},
  selectionEnabled: false,
  allowClickOnIcon: false,
  selectedClients: [],
  updateRecentClients: () => {},
  beforeGoToProfilePage: () => null,
  classes: {},
};

export default React.memo(ClientListItemGeneral);
